import { useCallback, useEffect, useMemo, useState } from 'react';
import api from 'utils/api';
import { useToast } from 'contexts/ToastContext';
import {
  DAY_ACTION_LABELS,
  DAY_OPENING_ENDPOINTS,
  getActionPayload,
  getDayConfirmCopy,
  getOpenDay,
  parseDayOpeningListResponse
} from 'utils/dayOpening';

export default function useDayOpening({ autoFetch = true } = {}) {
  const { showToast, showApiError } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(autoFetch);
  const [acting, setActing] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const openDay = useMemo(() => getOpenDay(rows), [rows]);
  const hasClosedRecords = useMemo(() => rows.some((row) => row.close_time != null), [rows]);

  const fetchList = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(DAY_OPENING_ENDPOINTS.list);
      const ok = res.data?.status !== false;
      if (ok) {
        setRows(parseDayOpeningListResponse(res));
      } else {
        setRows([]);
        showToast(res.data?.message || 'Failed to load day openings', { severity: 'error' });
      }
    } catch (err) {
      setRows([]);
      if (!silent) showApiError(err, 'Failed to load day openings');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showApiError, showToast]);

  useEffect(() => {
    if (autoFetch) fetchList();
  }, [autoFetch, fetchList]);

  const runAction = useCallback(async (action) => {
    setActing(action);
    try {
      const payload = getActionPayload(action, openDay);
      const res = await api.post(DAY_OPENING_ENDPOINTS[action], payload);
      if (res.data?.status === false) {
        showToast(res.data?.message || 'Action failed', { severity: 'error' });
        return;
      }
      showToast(res.data?.message || DAY_ACTION_LABELS[action], { severity: 'success' });
      setConfirmAction(null);
      await fetchList(true);
    } catch (err) {
      showApiError(err, 'Action failed');
    } finally {
      setActing(null);
    }
  }, [fetchList, openDay, showApiError, showToast]);

  const handleActionClick = useCallback((action) => {
    if (action === 'start') {
      runAction('start');
      return;
    }
    setConfirmAction(action);
  }, [runAction]);

  const confirmCopy = confirmAction ? getDayConfirmCopy(confirmAction) : null;

  const canStart = !openDay && !hasClosedRecords;
  const canClose = Boolean(openDay);
  const canRestart = !openDay && hasClosedRecords;

  return {
    rows,
    loading,
    acting,
    confirmAction,
    setConfirmAction,
    openDay,
    hasClosedRecords,
    canStart,
    canClose,
    canRestart,
    fetchList,
    runAction,
    handleActionClick,
    confirmCopy
  };
}
